import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  getPaymentVoucher,
  voidPaymentVoucher,
  voidRefusalMessage,
  VOID_REASON_MAX,
  VOID_REASON_MIN,
} from '../lib/accountsPayablePaymentsApi';
import type { PaymentVoucher } from '../lib/accountsPayablePaymentsApi';
import LoadingState from '../components/LoadingState';
import { usePermission } from '../hooks/usePermission';
import { formatDate as fmtDate, formatDateTime as fmtDateTime, storedDay } from "../utils/dateFormat";
import { downloadObjectsCsv } from '../utils/downloadCsv';
// A bill's money: TT$ / US$ from the label owner, never the locale's bare "$" (lane 5, V3d).
import { formatBillMoney as formatMoney } from '../features/accountsPayableReports/format';

// Invoice, due and payment dates are stored CALENDAR DAYS (UTC midnight): print their own day (D109.6).
function formatStoredDay(value?: string | null) {
  return fmtDate(storedDay(value), '--');
}

function formatDateTime(value?: string | null) {
  return fmtDateTime(value, '--');
}


export const AccountsPayablePaymentVoucherPage: React.FC = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payment, setPayment] = useState<PaymentVoucher | null>(null);
  // Voiding needs the FINANCIALS edit right (the server enforces it; this only
  // hides a button that would be refused).
  const { canEdit } = usePermission('financials');
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState('');

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const res = await getPaymentVoucher(id);
      setPayment(res.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load payment voucher.');
      setPayment(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isVoid = payment?.status === 'void';
  const reasonLength = voidReason.trim().length;
  const reasonOk = reasonLength >= VOID_REASON_MIN && reasonLength <= VOID_REASON_MAX;

  const closeVoid = () => {
    if (voidBusy) return;
    setVoidOpen(false);
    setVoidReason('');
    setVoidError('');
  };

  // A refusal is explained by its CODE (lib/accountsPayablePaymentsApi.ts); the
  // server's sentence is never shown. On success the voucher is read again, so the
  // banner, the status and the credit panel all come from the server's own record.
  const confirmVoid = async () => {
    if (!id || !reasonOk || voidBusy) return;
    setVoidBusy(true);
    setVoidError('');
    try {
      await voidPaymentVoucher(id, voidReason);
      setVoidOpen(false);
      setVoidReason('');
      await load();
    } catch (e: unknown) {
      setVoidError(voidRefusalMessage(e));
    } finally {
      setVoidBusy(false);
    }
  };

  const allocations = useMemo(() => (Array.isArray(payment?.allocations) ? payment?.allocations ?? [] : []), [payment]);
  const totalAllocated = useMemo(
    () => allocations.reduce((sum: number, row: any) => sum + Number(row.amount || 0), 0),
    [allocations]
  );
  const exportRows = useMemo(
    () =>
      allocations.map((row: any, idx: number) => ({
        'Line #': idx + 1,
        'Invoice #': row.invoiceNumber || '--',
        Supplier: row.supplierName || payment?.supplierName || '--',
        'Invoice Date': formatStoredDay(row.invoiceDate),
        'Due Date': formatStoredDay(row.dueDate),
        'Outstanding Before': row.outstandingBeforePayment ?? '',
        'Allocated Amount': row.amount ?? 0,
        Currency: row.currency || payment?.currency || 'TTD',
        Note: row.note || '',
      })),
    [allocations, payment?.currency, payment?.supplierName]
  );

  return (
    <div className="voucher-print-root h-full bg-brand-bg overflow-auto">
      <style>{`
        @page {
          size: landscape;
          margin: 10mm;
        }
        @media print {
          body * {
            visibility: hidden !important;
          }
          .voucher-print-root, .voucher-print-root * {
            visibility: visible !important;
          }
          .voucher-print-root {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: auto !important;
            background: white !important;
          }
          .voucher-card {
            break-inside: avoid;
          }
          .voucher-table-wrap {
            overflow: visible !important;
          }
          .voucher-table {
            min-width: 0 !important;
            width: 100% !important;
            table-layout: fixed !important;
            font-size: 9px !important;
          }
          .voucher-table th,
          .voucher-table td {
            padding: 3px 4px !important;
            white-space: normal !important;
            word-break: break-word;
            overflow-wrap: anywhere;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      <div className="no-print px-8 pt-4 pb-3 bg-brand-bg border-b border-slate-200">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex h-9 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft aria-hidden="true" className="h-4 w-4" />
              Back
            </button>
            <div>
              <h1 className="text-xl font-semibold leading-7 text-slate-900">Payment voucher</h1>
              <p className="text-xs text-slate-500">
                Saved payment record for supplier invoices. Print or export as PDF from the browser.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadObjectsCsv(`payment-voucher-${payment?.reference || id}.csv`, exportRows)}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
            >
              Export CSV
            </button>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600"
            >
              Print Voucher
            </button>
            {payment && !isVoid && canEdit ? (
              <button
                onClick={() => {
                  setVoidError('');
                  setVoidOpen(true);
                }}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-rose-300 bg-white text-rose-700 text-sm font-semibold hover:bg-rose-50"
              >
                Void payment
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl p-8">
        {loading ? (
          <div className="rounded-xl border border-slate-200 bg-white p-8"><LoadingState text="Loading voucher..." fullHeight={false} size={72} /></div>
        ) : error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-700">{error}</div>
        ) : payment ? (
          <div className="space-y-4">
            {isVoid ? (
              // Not `no-print`: a voided voucher must never print as if it still stood.
              <div
                role="status"
                data-testid="void-banner"
                className="voucher-card rounded-xl border border-rose-300 bg-rose-50 p-5 text-rose-900"
              >
                <div className="text-sm font-semibold uppercase tracking-wide">Voided</div>
                <p className="mt-1 text-sm">
                  This payment was voided on {formatDateTime(payment.voidedAt)}
                  {payment.voidedBy ? ` by ${payment.voidedBy}` : ''}. Each bill it paid was put back to what it owed.
                </p>
                <p className="mt-1 text-sm">
                  <span className="font-semibold">Reason:</span> {payment.voidReason || '--'}
                </p>
              </div>
            ) : null}
            <div className="voucher-card rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Payment Voucher{' '}
                    <span
                      data-testid="voucher-status"
                      className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${
                        isVoid ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'
                      }`}
                    >
                      {isVoid ? 'Void' : 'Applied'}
                    </span>
                  </div>
                  <h2 className={`text-base font-semibold leading-6 ${isVoid ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                    {payment.reference || 'Payment Voucher'}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Supplier: {payment.supplierName || '--'} - Payment Date: {formatStoredDay(payment.paymentDate)} - Method:{' '}
                    {payment.method || '--'}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    Cheque #: {payment.chequeNumber || '--'} - Online Ref: {payment.onlineReferenceNumber || '--'}
                  </p>
                  {payment.paymentRunId && payment.paymentRunNumber ? (
                    <p className="mt-1 text-sm text-slate-500">
                      Recorded in payment run{' '}
                      <button
                        type="button"
                        className="text-primary-600 hover:underline"
                        onClick={() => navigate(`/accounts/payment-runs/${payment.paymentRunId}`)}
                      >
                        {payment.paymentRunNumber}
                      </button>
                    </p>
                  ) : null}
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">Voucher Generated</div>
                  <div className="text-sm font-semibold text-slate-800">{formatDateTime(payment.createdAt)}</div>
                </div>
              </div>

              <div className={`mt-6 grid grid-cols-1 gap-3 ${payment.credit ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                <Stat label="Total Payment" value={formatMoney(payment.amount, payment.currency || 'TTD')} />
                <Stat label="Allocated" value={formatMoney(totalAllocated, payment.currency || 'TTD')} />
                <Stat label="Unallocated" value={formatMoney(payment.unallocatedAmount || 0, payment.currency || 'TTD')} />
                {payment.credit ? (
                  <Stat label="Held as credit" value={formatMoney(payment.credit.amount, payment.credit.currency || payment.currency || 'TTD')} />
                ) : null}
              </div>

              {payment.note ? (
                <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold text-slate-500">Payment Note</div>
                  <div className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{payment.note}</div>
                </div>
              ) : null}
            </div>

            {payment.credit ? (
              <div
                data-testid="payment-credit"
                className="voucher-card rounded-xl border border-purple-200 bg-purple-50 p-5 text-purple-900"
              >
                <div className="text-sm font-semibold">Supplier credit created by this payment</div>
                <p className="mt-1 text-sm">
                  {payment.credit.vendorName || 'The supplier'} was credited{' '}
                  {formatMoney(payment.credit.amount, payment.credit.currency || 'TTD')} from what was paid over the bills.
                  {payment.credit.status === 'reversed'
                    ? ' The credit was taken back when this payment was voided.'
                    : payment.credit.remaining >= payment.credit.amount - 0.005
                      ? ` None of it has been used yet: it waits for their next bill.`
                      : payment.credit.remaining > 0.005
                        ? ` ${formatMoney(payment.credit.amount - payment.credit.remaining, payment.credit.currency || 'TTD')} has paid down their other bills, and ${formatMoney(payment.credit.remaining, payment.credit.currency || 'TTD')} waits for their next bill.`
                        : ' All of it has paid down their other bills.'}
                </p>
                {(payment.credit.applications || []).length > 0 ? (
                  <ul className="mt-2 list-disc pl-5 text-sm">
                    {(payment.credit.applications || []).map((a, idx) => (
                      <li key={`${a.accountsPayableId}-${idx}`}>
                        {a.invoiceNumber || a.accountsPayableId}: {formatMoney(a.amount, payment.credit?.currency || 'TTD')}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {!isVoid && payment.credit.remaining < payment.credit.amount - 0.005 ? (
                  <p className="mt-2 text-xs text-purple-700">
                    Because this credit has been used, the payment cannot be voided.
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="voucher-card rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <div className="text-sm font-semibold text-slate-800">Invoice Allocations</div>
              </div>
              <div className="voucher-table-wrap overflow-auto">
                <table className="voucher-table data-table-grid min-w-[1000px] w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-xs text-slate-600">
                      <th className="px-4 py-3">#</th>
                      <th className="px-4 py-3">Invoice #</th>
                      <th className="px-4 py-3">Invoice Date</th>
                      <th className="px-4 py-3">Due Date</th>
                      <th className="px-4 py-3">Outstanding Before</th>
                      <th className="px-4 py-3">Allocated</th>
                      <th className="px-4 py-3">Currency</th>
                      <th className="px-4 py-3">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocations.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                          No allocations found on this payment.
                        </td>
                      </tr>
                    ) : (
                      allocations.map((row: any, idx: number) => (
                        <tr key={`${row.accountsPayableId || idx}`} className="border-b border-slate-100">
                          <td className="px-4 py-3 text-slate-700">{idx + 1}</td>
                          <td className="px-4 py-3 font-medium text-slate-800">{row.invoiceNumber || '--'}</td>
                          <td className="px-4 py-3 text-slate-700">{formatStoredDay(row.invoiceDate)}</td>
                          <td className="px-4 py-3 text-slate-700">{formatStoredDay(row.dueDate)}</td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                            {formatMoney(row.outstandingBeforePayment, row.currency || payment.currency || 'TTD')}
                          </td>
                          <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                            {formatMoney(row.amount, row.currency || payment.currency || 'TTD')}
                          </td>
                          <td className="px-4 py-3 text-slate-700">{row.currency || payment.currency || 'TTD'}</td>
                          <td className="px-4 py-3 text-slate-700">{row.note || '--'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {voidOpen && payment ? (
        <div className="no-print fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="void-payment-title"
            className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl"
          >
            <h2 id="void-payment-title" className="text-base font-semibold leading-6 text-slate-900">
              Void payment {payment.reference}?
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              The payment stays on record, marked void. Each bill it paid goes back to what it owed
              {payment.credit ? ', and the supplier credit it created is taken back' : ''}. This cannot be undone: to pay
              again, record a new payment.
            </p>
            <label htmlFor="void-reason" className="mt-4 block text-sm font-semibold text-slate-700">
              Reason (required)
            </label>
            <textarea
              id="void-reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              maxLength={VOID_REASON_MAX}
              rows={3}
              placeholder="For example: cheque bounced, paid the wrong supplier"
              className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm outline-none focus:border-cyan-400"
            />
            <div className="mt-1 text-xs text-slate-500">
              {VOID_REASON_MIN} to {VOID_REASON_MAX} characters
            </div>
            {voidError ? (
              <div role="alert" className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                {voidError}
              </div>
            ) : null}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeVoid}
                disabled={voidBusy}
                className="h-10 rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmVoid()}
                disabled={!reasonOk || voidBusy}
                className="h-10 rounded-md bg-rose-600 px-4 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {voidBusy ? 'Voiding...' : 'Void payment'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
    </div>
  );
}
